import { Link } from "react-router-dom";
import AnimateOnScroll from "../AnimateOnScroll";

interface TextSectionProps {
  heading: string;
  body: string;
  cta?: { label: string; href: string }[];
}

const renderInlineMarkdown = (text: string) => {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={i} className="italic text-foreground/80">{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

const TextSection = ({ heading, body, cta }: TextSectionProps) => {
  const paragraphs = body.split("\n\n");

  return (
    <section className="mx-auto max-w-4xl px-6 py-12 md:py-16">
      <AnimateOnScroll>
        <h2 className="text-3xl font-bold md:text-4xl">{heading}</h2>
      </AnimateOnScroll>
      <AnimateOnScroll delay="delay-200">
        <div className="mt-6 space-y-4">
          {paragraphs.map((p, i) => {
            const lines = p.split("\n");
            const isList = lines.length > 1 && lines.every((l) => l.trimStart().startsWith("- "));
            if (isList) {
              return (
                <ul key={i} className="list-disc space-y-2 pl-6 text-lg leading-relaxed font-body text-muted-foreground marker:text-primary/60">
                  {lines.map((line, j) => (
                    <li key={j}>{renderInlineMarkdown(line.trimStart().slice(2))}</li>
                  ))}
                </ul>
              );
            }
            const trimmed = p.trim();
            const isQuote = trimmed.startsWith("*") && trimmed.endsWith("*") && !trimmed.slice(1, -1).includes("*");
            return (
              <p key={i} className={`text-lg leading-relaxed font-body ${isQuote ? "border-l-4 border-primary/30 pl-5 text-foreground/80" : "text-muted-foreground"}`}>
                {renderInlineMarkdown(p)}
              </p>
            );
          })}
        </div>
      </AnimateOnScroll>
      {cta && cta.length > 0 && (
        <AnimateOnScroll delay="delay-300">
          <div className="mt-10 flex flex-wrap gap-4">
            {cta.map((item, i) => (
              <Link
                key={i}
                to={item.href}
                className={`inline-flex items-center rounded-full px-8 py-3.5 font-body font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 ${
                  i === 0
                    ? "bg-primary text-primary-foreground shadow-lg hover:opacity-90"
                    : "border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </AnimateOnScroll>
      )}
    </section>
  );
};

export default TextSection;
